import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { Models } from "api/types";
import { ShopNowAffirm } from "atoms/affirm/shopNowAffirm/ShopNowAffirm";
import { Icon } from "atoms/icon/Icon";
import Shop from "atoms/icon/icons/shop_nav.svg";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { AdvisorMissingOutOverlay } from "modules/adonis/AdvisorMissingOut/AdvisorMissingOutOverlay";
import { AdvisorShopCategoryLink } from "modules/adonis/AdvisorShopCategoryLink/AdvisorShopCategoryLink";
import { PersonalisationIntroContainer_PATH } from "modules/personalisation/containers/PersonalisationIntroContainer";
import { useUserPreferences } from "modules/user/useUser/useUser";
import { ProductCategory } from "molecules/containers/product-category/ProductCategory";
import { IfFlag, useFeatureFlag } from "services/Flags";
import { getCustomCategoryDestinationUrl } from "toolbox/CustomCategories";

export interface CategoriesSectionProps {
  categories: Models<"CategoryResult">[];
  customCategories: Models<"CategoryListingsResult">[];
  hasMemberships: boolean;
  hasTreatments: boolean;
  hasPackages: boolean;
  affirmEnabled: boolean | undefined;
  hasRetailProducts: boolean;
  isGiftCardsEnabled: boolean;
  giftCards: Models<"DisplayGiftCardListing">[] | undefined;
}

const CategoriesSection = ({
  categories,
  hasMemberships,
  customCategories,
  hasPackages,
  hasTreatments,
  affirmEnabled,
  hasRetailProducts,
  isGiftCardsEnabled,
  giftCards,
}: CategoriesSectionProps) => {
  const defaultMemberships = categories.find(
    (c) => c.defaultCategoryId === "memberships"
  );
  const defaultTreatments = categories.find(
    (c) => c.defaultCategoryId === "treatments"
  );
  const defaultPackages = categories.find(
    (c) => c.defaultCategoryId === "packages"
  );
  const defaultProducts = categories.find(
    (c) => c.defaultCategoryId === "selling-products"
  );
  const defaultGiftCards = categories.find(
    (c) => c.defaultCategoryId === "giftcards"
  );
  const navigate = useNavigate();

  const [showCategoriesOverlay, setShowCategoriesOverlay] = useState(false);

  const { preferences } = useUserPreferences();
  const { isPersonalisationEnabled } = useAdonisChat();
  const adonisShopCategoryLinkText = useFeatureFlag(
    "adonisShopCategoryLinkText"
  );

  let defaultGiftCardImage = "";
  if (defaultGiftCards?.imageUrl) {
    defaultGiftCardImage = defaultGiftCards.imageUrl;
  } else if (giftCards && giftCards.length > 0) {
    defaultGiftCardImage =
      giftCards[0].imageUri.substring(
        0,
        giftCards[0].imageUri.lastIndexOf("/") + 1
      ) + "giftcard-justforyou.webp";
  }

  const hasCustomShopCategoriesWithGiftCards =
    isGiftCardsEnabled &&
    customCategories.some((csc) =>
      csc.categoryInclusions.some(
        (ci) =>
          ci.packageListingId &&
          giftCards?.some((gc) => gc.id === ci.packageListingId)
      )
    );

  const onAdvisorLinkClick = () => {
    if (preferences?.concernIds && preferences.concernIds.length > 0) {
      navigate("/shop/recommended-treatments");
    } else {
      setShowCategoriesOverlay(true);
    }
  };

  return (
    <AdvisorMissingOutOverlay
      showOverlay={
        showCategoriesOverlay &&
        isPersonalisationEnabled &&
        (!preferences?.concernIds || preferences.concernIds.length === 0)
      }
      onButtonClick={() => navigate(PersonalisationIntroContainer_PATH)}
      onClose={() => setShowCategoriesOverlay(false)}
    >
      <div className="flex flex-col items-center bg-two p-6">
        <div className="flex flex-col items-center">
          <Icon size="xsmall" svg={Shop} color="brandColor" />
          <div className="py-4 font-serif text-h2">Our Services</div>
          {affirmEnabled && (hasPackages || hasTreatments) && (
            <div className="pb-6">
              <ShopNowAffirm />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <IfFlag flag="site-wide-promotions">
            {customCategories.map((customCategory) => (
              <ProductCategory
                key={customCategory.category.id}
                text={customCategory.category.title ?? ""}
                secondaryText={customCategory.category.description ?? ""}
                imageUrl={customCategory.category.imageUrl || ""}
                backgroundColor={
                  customCategory.promotion?.backgroundColor ?? ""
                }
                color={customCategory.promotion?.textColor ?? ""}
                onClick={() => {
                  const customShopCategoryTo = getCustomCategoryDestinationUrl(
                    customCategory,
                    hasCustomShopCategoriesWithGiftCards
                  );

                  navigate(customShopCategoryTo);
                }}
              />
            ))}
          </IfFlag>
          {isPersonalisationEnabled && (
            <AdvisorShopCategoryLink
              linkText={adonisShopCategoryLinkText}
              onClick={onAdvisorLinkClick}
            />
          )}
          {hasMemberships && defaultMemberships && (
            <ProductCategory
              key={defaultMemberships.defaultCategoryId}
              text={defaultMemberships.title}
              secondaryText={defaultMemberships.description}
              imageUrl={defaultMemberships.imageUrl || ""}
              onClick={() => navigate("/shop/memberships")}
            />
          )}
          {hasTreatments && defaultTreatments && (
            <ProductCategory
              key={defaultTreatments.defaultCategoryId}
              text={defaultTreatments.title}
              secondaryText={defaultTreatments.description}
              imageUrl={defaultTreatments.imageUrl || ""}
              onClick={() => navigate("/shop/treatments")}
            />
          )}
          {hasPackages && defaultPackages && (
            <ProductCategory
              key={defaultPackages.defaultCategoryId}
              text={defaultPackages.title}
              secondaryText={defaultPackages.description}
              imageUrl={defaultPackages.imageUrl || ""}
              onClick={() => navigate("/shop/packages")}
            />
          )}
          <IfFlag flag="selling-products">
            {hasRetailProducts && defaultProducts && (
              <ProductCategory
                key={defaultProducts.defaultCategoryId || "selling-products"}
                text={defaultProducts.title || "Products"}
                secondaryText={
                  defaultProducts.description ||
                  "Shop skincare and beauty essentials to elevate your at-home routine"
                }
                imageUrl={defaultProducts.imageUrl || ""}
                onClick={() => navigate("/shop/products")}
              />
            )}
          </IfFlag>
          {isGiftCardsEnabled &&
            giftCards &&
            !!giftCards.length &&
            defaultGiftCards && (
              <ProductCategory
                key={defaultGiftCards.defaultCategoryId || "giftcards"}
                text={defaultGiftCards.title || "Gift Cards"}
                secondaryText={
                  defaultGiftCards.description ||
                  "A simple way to give the perfect present"
                }
                imageUrl={defaultGiftCardImage || ""}
                onClick={() => navigate("/shop/gift-cards")}
              />
            )}
        </div>
      </div>
    </AdvisorMissingOutOverlay>
  );
};

export default CategoriesSection;
